/**
 * Banking & Reconciliation (design §5) — 3-pane workbench: imported lines
 * (left) · match/create (center) · balance + rules (right). Add a bank account,
 * import a statement (manual rows → CSV → backend auto-match), then reconcile
 * each line against a real payment (resolve) or ignore it. All actions hit the
 * banking service; the reconciled balance recomputes from live line status.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { fmtMoney, money } from '../utils/format';
import SkIcon from '../components/skIcon';
import ModalShell from '../components/modals/modalShell';
import DateField from '../components/dateField';
import { showToast } from '../components/skToast';
import { listBankAccounts, createBankAccount, importStatement, listBankLines, listBankRules, resolveBankLine } from '../services/billing/banking.service';
import { listPayments } from '../services/billing/payment.service';
import { BankAccount } from '../schema/billing/banking';
import { Payment } from '../schema/billing/payment';
import { BankLineStatus } from '../schema/billing/common';

interface Line { id: string; date: string; desc: string; amount: number; status: BankLineStatus; matchedPaymentId?: string | null; candidates: string[]; }

const SC_TEXT: Record<BankLineStatus, string> = {
  UNMATCHED: 'Unmatched', RULE_MATCHED: 'Rule matched', AI_SUGGESTED: 'Suggested', MATCHED: 'Reconciled', CREATED: 'Created', IGNORED: 'Ignored',
};

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
// Import CTA — indigo→violet gradient, consistent with the app's primary actions.
const CTA_A = '#6366f1';
const CTA_B = '#8b5cf6';

/** Hover-reactive imported bank line. */
const LineRow: React.FC<{ desc: string; date: string; amount: number; sc: string; label: string; selected: boolean; onPress: () => void }> = ({ desc, date, amount, sc, label, selected, onPress }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 12, paddingHorizontal: 13, borderBottomWidth: 1, borderColor: colors.line,
        backgroundColor: selected ? colors.accentSoft : hover ? colors.paper2 : colors.card,
        borderLeftWidth: 3, borderLeftColor: selected ? colors.accent : sc,
        ...web({ transition: 'background-color .12s ease, border-color .12s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: sc, ...web({ boxShadow: `0 0 0 3px ${sc}22` }) }} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: selected ? colors.accentInk : colors.ink }}>{desc}</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: sc, marginTop: 1 }}>{label} · {date}</Text>
      </View>
      <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: amount < 0 ? colors.ink2 : colors.success }}>{(amount < 0 ? '−' : '+') + fmtMoney(Math.abs(amount), cur)}</Text>
    </Pressable>
  );
};

const Banking: React.FC = () => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { isWide } = useResponsive();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [rules, setRules] = useState<{ id: string; name: string; priority: number }[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [pickPaymentId, setPickPaymentId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | BankLineStatus>('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    return Promise.allSettled([listBankAccounts(), listBankLines(), listBankRules(), listPayments()])
      .then(([a, l, r, p]) => {
        if (a.status === 'fulfilled' && Array.isArray(a.value)) {
          setAccounts(a.value);
          setAccountId((cur) => cur || a.value[0]?.id || null);
        }
        if (l.status === 'fulfilled' && Array.isArray(l.value)) {
          const mapped: Line[] = l.value.map((x: any) => ({
            id: x.id, date: x.line_date || '', desc: x.description || '—', amount: money(x.amount),
            status: x.status, matchedPaymentId: x.matched_payment_id,
            candidates: Array.isArray(x.ai_suggestion?.candidates) ? x.ai_suggestion.candidates : [],
          }));
          setLines(mapped);
          setSelId((cur) => cur || mapped[0]?.id || null);
        }
        if (r.status === 'fulfilled' && Array.isArray(r.value)) setRules(r.value.map((x: any) => ({ id: x.id, name: x.name, priority: x.priority })));
        if (p.status === 'fulfilled' && Array.isArray(p.value)) setPayments(p.value);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = filter === 'all' ? lines : lines.filter((l) => l.status === filter);
  const sel = lines.find((l) => l.id === selId) || lines[0] || null;
  const matched = lines.filter((l) => l.status === 'MATCHED');
  const resolved = lines.filter((l) => l.status === 'MATCHED' || l.status === 'IGNORED'); // handled either way
  const reconciled = matched.reduce((s, l) => s + l.amount, 0);
  const statement = lines.reduce((s, l) => s + l.amount, 0);
  // Difference = sum of lines still needing attention (unmatched/suggested). Hits ₹0 when all lines are handled.
  const diff = lines.filter((l) => l.status !== 'MATCHED' && l.status !== 'IGNORED').reduce((s, l) => s + l.amount, 0);

  // Candidate payments for the selected line: same direction, exact-amount first.
  const candidates = useMemo(() => {
    if (!sel) return [] as (Payment & { amt: number })[];
    const dir = sel.amount >= 0 ? 'RECEIVED' : 'MADE';
    const abs = Math.abs(sel.amount);
    return payments
      .filter((p) => p.direction === dir)
      .map((p) => ({ ...p, amt: money(p.amount) }))
      .sort((a, b) => Math.abs(a.amt - abs) - Math.abs(b.amt - abs) || (b.payment_date > a.payment_date ? 1 : -1));
  }, [sel, payments]);

  // Default the picker to the matched/suggested/exact-amount payment when the line changes.
  useEffect(() => {
    if (!sel) { setPickPaymentId(null); return; }
    const def = sel.matchedPaymentId || sel.candidates[0] || candidates.find((c) => Math.abs(c.amt - Math.abs(sel.amount)) < 0.005)?.id || null;
    setPickPaymentId(def);
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doMatch = () => {
    if (!sel || !pickPaymentId || busy) return;
    setBusy(true);
    resolveBankLine(sel.id, { type: 'match', payment_id: pickPaymentId })
      .then(() => reload())
      .finally(() => setBusy(false));
  };
  const doIgnore = () => {
    if (!sel || busy) return;
    setBusy(true);
    resolveBankLine(sel.id, { type: 'ignore' })
      .then(() => reload())
      .finally(() => setBusy(false));
  };

  const filters = useMemo(() => [
    { id: 'all' as const, label: 'All', count: lines.length },
    { id: 'UNMATCHED' as const, label: 'Unmatched', count: lines.filter((l) => l.status === 'UNMATCHED').length },
    { id: 'AI_SUGGESTED' as const, label: 'Suggested', count: lines.filter((l) => l.status === 'AI_SUGGESTED').length },
    { id: 'MATCHED' as const, label: 'Reconciled', count: matched.length },
    { id: 'IGNORED' as const, label: 'Ignored', count: lines.filter((l) => l.status === 'IGNORED').length },
  ], [lines]);

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;
  const statusColor = (s: BankLineStatus) => (s === 'MATCHED' ? colors.success : s === 'AI_SUGGESTED' || s === 'RULE_MATCHED' ? colors.info : s === 'IGNORED' ? colors.ink4 : colors.warn);
  const pmtLabel = (p: Payment) => `${p.mode?.replace('_', ' ') || 'Payment'} · ${p.payment_date}${p.reference ? ' · ' + p.reference : ''}`;

  // No bank account yet → onboarding card.
  if (!loading && accounts.length === 0) {
    return (
      <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
        <View style={[card, { paddingVertical: 64, paddingHorizontal: 24, alignItems: 'center' }]}>
          <View style={{ width: 96, height: 96, borderRadius: 30, alignItems: 'center', justifyContent: 'center', marginBottom: 20, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `radial-gradient(circle at 50% 32%, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 14px 38px -10px ${colors.accent}4d` }) }}>
            <View style={{ width: 58, height: 58, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 6px 16px -6px ${colors.accent}66` }) }}>
              <SkIcon name="bank" size={26} color={colors.accentInk} strokeWidth={2} />
            </View>
          </View>
          <Text style={{ fontFamily: font.sansBold, fontSize: 18, fontWeight: '700', color: colors.ink, letterSpacing: -0.3 }}>No bank account yet</Text>
          <Text style={{ fontFamily: font.sans, fontSize: 13.5, color: colors.ink3, textAlign: 'center', maxWidth: 400, lineHeight: 20, marginTop: 6 }}>
            Add a bank account, then import a statement to reconcile it against your recorded payments.
          </Text>
          <Pressable onPress={() => setAddOpen(true)} style={{ height: 38, paddingHorizontal: 16, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 20, backgroundColor: CTA_A, ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: `0 6px 14px -4px ${CTA_B}88`, cursor: 'pointer' }) }}>
            <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Add bank account</Text>
          </Pressable>
        </View>
        <AddAccountModal visible={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); reload(); }} />
      </View>
    );
  }

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Toolbar: account + actions */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <View style={{ flexDirection: 'row', gap: 7, flexWrap: 'wrap' }}>
          {accounts.map((a) => {
            const act = a.id === accountId;
            return (
              <Pressable key={a.id} onPress={() => setAccountId(a.id)} style={{ height: 36, paddingHorizontal: 13, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, borderWidth: 1, borderColor: act ? colors.accentLine : colors.line, backgroundColor: act ? colors.accentSoft : colors.card, ...theme.shadow('sm'), ...web({ cursor: 'pointer', ...(act ? { backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` } : {}) }) }}>
                <SkIcon name="bank" size={14} color={act ? colors.accentInk : colors.ink4} strokeWidth={2} />
                <Text style={{ fontFamily: act ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: act ? '600' : '500', color: act ? colors.accentInk : colors.ink2 }}>{a.name}</Text>
              </Pressable>
            );
          })}
          <Pressable onPress={() => setAddOpen(true)} style={{ height: 36, paddingHorizontal: 12, borderRadius: 10, justifyContent: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, flexDirection: 'row', alignItems: 'center', gap: 5, ...web({ cursor: 'pointer' }) }}>
            <SkIcon name="plus" size={14} color={colors.ink3} strokeWidth={2.2} />
            <Text style={{ fontFamily: font.sansMedium, fontSize: 12.5, color: colors.ink3 }}>Account</Text>
          </Pressable>
        </View>
        <Pressable onPress={() => (accountId ? setImportOpen(true) : showToast('warn', 'Add an account first', 'Create a bank account before importing'))} style={{ height: 40, paddingHorizontal: 16, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: CTA_A, ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: `0 5px 12px -3px ${CTA_B}77`, cursor: 'pointer' }) }}>
          <SkIcon name="upload" size={15} color="#fff" strokeWidth={2.2} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Import statement</Text>
        </Pressable>
      </View>

      {/* Filter segmented control */}
      <View style={{ flexDirection: 'row', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', gap: 3, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
          {filters.map((f) => {
            const a = filter === f.id;
            return (
              <Pressable key={f.id} onPress={() => setFilter(f.id)} style={{ height: 30, paddingHorizontal: 13, borderRadius: 7, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: a ? colors.card : 'transparent', ...(a ? web({ boxShadow: '0 1px 3px rgba(16,24,40,.10)' }) : {}), ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}>
                <Text style={{ fontFamily: a ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: a ? '600' : '500', color: a ? colors.ink : colors.ink3 }}>{f.label}</Text>
                <View style={{ minWidth: 18, height: 17, paddingHorizontal: 5, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: a ? colors.accentSoft : colors.paper3 }}>
                  <Text style={{ fontFamily: font.monoMedium, fontSize: 10, color: a ? colors.accentInk : colors.ink4 }}>{f.count}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 14 }}>
        {/* Imported lines */}
        <View style={[card, { flex: isWide ? 1.1 : undefined, overflow: 'hidden' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 13, paddingHorizontal: 15, borderBottomWidth: 1, borderColor: colors.line }}>
            <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
              <SkIcon name="bank" size={15} color={colors.accentInk} strokeWidth={2} />
            </View>
            <Text style={{ fontFamily: font.sansBold, fontSize: 13.5, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Statement lines</Text>
            <View style={{ flex: 1 }} />
            <View style={{ paddingHorizontal: 9, height: 21, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 10.5, color: colors.ink3 }}>{shown.length}</Text>
            </View>
          </View>
          {loading ? (
            <View style={{ padding: 40, alignItems: 'center' }}><Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text></View>
          ) : shown.length === 0 ? (
            <View style={{ paddingVertical: 48, paddingHorizontal: 24, alignItems: 'center' }}>
              <View style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <SkIcon name="upload" size={20} color={colors.ink4} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansSemibold, fontWeight: '600', fontSize: 14, color: colors.ink2 }}>No bank lines</Text>
              <Text style={{ fontFamily: font.sans, fontSize: 12.5, color: colors.ink3, textAlign: 'center', marginTop: 4 }}>Import a statement to start reconciling.</Text>
            </View>
          ) : (
            <ScrollView style={{ maxHeight: 460 }}>
              {shown.map((l) => (
                <LineRow
                  key={l.id}
                  desc={l.desc}
                  date={l.date}
                  amount={l.amount}
                  sc={statusColor(l.status)}
                  label={SC_TEXT[l.status]}
                  selected={sel != null && l.id === sel.id}
                  onPress={() => setSelId(l.id)}
                />
              ))}
            </ScrollView>
          )}
        </View>

        {/* Center: match / ignore */}
        <View style={[card, { flex: isWide ? 1.2 : undefined, padding: 16 }]}>
          {!sel ? (
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Select a line to reconcile.</Text>
          ) : (
            <>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 14, fontWeight: '600', color: colors.ink }}>{sel.desc}</Text>
              <Text style={{ fontFamily: font.monoSemibold, fontSize: 20, fontWeight: '600', color: sel.amount < 0 ? colors.ink2 : colors.success, marginVertical: 8 }}>{(sel.amount < 0 ? '−' : '+') + fmtMoney(Math.abs(sel.amount), cur)}</Text>

              {sel.status === 'MATCHED' ? (
                <View style={{ padding: 12, borderRadius: 5, backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <SkIcon name="checkCircle" size={15} color={colors.success} strokeWidth={2} />
                  <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.success, fontWeight: '600' }}>Reconciled — matched to a payment</Text>
                </View>
              ) : sel.status === 'IGNORED' ? (
                <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>This line was ignored (e.g. bank charge, transfer). It won’t affect reconciliation.</Text>
              ) : (
                <View style={{ gap: 10 }}>
                  <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>
                    {sel.status === 'AI_SUGGESTED' ? 'Multiple possible payments — pick the right one and match.' : 'Match this line to a recorded payment, or ignore it.'}
                  </Text>
                  {candidates.length === 0 ? (
                    <View style={{ padding: 11, borderRadius: 5, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
                      <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>No matching {sel.amount >= 0 ? 'received' : 'made'} payment found. Record the payment first (Payments screen), or ignore this line.</Text>
                    </View>
                  ) : (
                    <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 5, overflow: 'hidden' }}>
                      {candidates.slice(0, 6).map((c) => {
                        const on = c.id === pickPaymentId;
                        const exact = Math.abs(c.amt - Math.abs(sel.amount)) < 0.005;
                        return (
                          <Pressable key={c.id} onPress={() => setPickPaymentId(c.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 9, paddingHorizontal: 11, borderBottomWidth: 1, borderColor: colors.line, backgroundColor: on ? colors.accentSoft : colors.card }}>
                            <View style={{ width: 15, height: 15, borderRadius: 8, borderWidth: 2, borderColor: on ? colors.accent : colors.line, alignItems: 'center', justifyContent: 'center' }}>
                              {on && <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.accent }} />}
                            </View>
                            <View style={{ flex: 1, minWidth: 0 }}>
                              <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: colors.ink }}>{pmtLabel(c)}</Text>
                              {!exact && <Text style={{ fontFamily: font.sans, fontSize: 10, color: colors.warn }}>amount differs</Text>}
                            </View>
                            <Text style={{ fontFamily: font.monoSemibold, fontSize: 12, fontWeight: '600', color: exact ? colors.success : colors.ink2 }}>{fmtMoney(c.amt, cur)}</Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  )}
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <Pressable
                      onPress={doMatch}
                      disabled={busy || !pickPaymentId}
                      style={{ flex: 1, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 7, backgroundColor: busy || !pickPaymentId ? colors.paper3 : colors.accent, ...(busy || !pickPaymentId ? {} : web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 6px 14px -4px ${colors.accent}88`, cursor: 'pointer' })) }}
                    >
                      <SkIcon name="checkCircle" size={15} color={busy || !pickPaymentId ? colors.ink4 : '#fff'} strokeWidth={2.2} />
                      <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: busy || !pickPaymentId ? colors.ink4 : '#fff' }}>{busy ? 'Working…' : 'Match payment'}</Text>
                    </Pressable>
                    <Pressable onPress={doIgnore} disabled={busy} style={{ height: 38, paddingHorizontal: 16, borderRadius: 10, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
                      <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Ignore</Text>
                    </Pressable>
                  </View>
                </View>
              )}
            </>
          )}
        </View>

        {/* Right: balance + rules */}
        <View style={{ flex: isWide ? 0.9 : undefined, gap: 14 }}>
          <View style={[card, { padding: 16 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 10 }}>
              <View style={{ width: 28, height: 28, borderRadius: 9, backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.successSoft}, ${colors.card})` }) }}>
                <SkIcon name="checkCircle" size={14} color={colors.success} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 12, color: colors.ink3, fontWeight: '600' }}>Reconciled balance</Text>
            </View>
            <Text style={{ fontFamily: font.monoSemibold, fontSize: 26, fontWeight: '700', color: colors.ink, letterSpacing: -0.5 }}>{fmtMoney(reconciled, cur)}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>Statement <Text style={{ fontFamily: font.monoMedium, color: colors.ink2 }}>{fmtMoney(statement, cur)}</Text></Text>
              <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: diff === 0 ? colors.successSoft : colors.warnSoft, borderWidth: 1, borderColor: diff === 0 ? colors.successLine : colors.warnLine }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: diff === 0 ? colors.success : colors.warn }}>Diff {(diff === 0 ? '' : diff < 0 ? '−' : '+') + fmtMoney(Math.abs(diff), cur)}</Text>
              </View>
            </View>
            <View style={{ height: 8, backgroundColor: colors.paper3, borderRadius: 999, marginTop: 12, overflow: 'hidden' }}>
              <View style={{ width: `${lines.length ? Math.round((resolved.length / lines.length) * 100) : 0}%`, height: 8, backgroundColor: colors.success, borderRadius: 999, ...web({ backgroundImage: `linear-gradient(90deg, ${colors.success}, ${colors.successLine})` }) }} />
            </View>
            <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 7 }}>{resolved.length} of {lines.length} lines handled · {matched.length} matched</Text>
          </View>
          <View style={[card, { padding: 16 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 10 }}>
              <View style={{ width: 28, height: 28, borderRadius: 9, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <SkIcon name="sliders" size={14} color={colors.accentInk} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 12, color: colors.ink3, fontWeight: '600' }}>Rules</Text>
            </View>
            {rules.length === 0 ? (
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4, lineHeight: 17 }}>No rules yet — recurring lines auto-match by amount + date.</Text>
            ) : (
              rules.map((r) => (
                <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 7, borderTopWidth: 1, borderColor: colors.line }}>
                  <View style={{ minWidth: 20, height: 20, paddingHorizontal: 5, borderRadius: 6, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ fontFamily: font.monoMedium, fontSize: 10.5, color: colors.ink4 }}>{r.priority}</Text>
                  </View>
                  <Text style={{ flex: 1, fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink }}>{r.name}</Text>
                </View>
              ))
            )}
          </View>
        </View>
      </View>

      <AddAccountModal visible={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); reload(); }} />
      <ImportStatementModal visible={importOpen} accountId={accountId} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); reload(); }} />
    </View>
  );
};

/* ── Add a bank account ──────────────────────────────────────────────── */
const AddAccountModal: React.FC<{ visible: boolean; onClose: () => void; onDone: () => void }> = ({ visible, onClose, onDone }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [name, setName] = useState('');
  const [code, setCode] = useState('1110');
  const [masked, setMasked] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (visible) { setName(''); setCode('1110'); setMasked(''); setBusy(false); } }, [visible]);

  const submit = async () => {
    if (busy) return;
    if (!name.trim()) { showToast('warn', 'Name required', 'Enter the bank account name'); return; }
    if (!code.trim()) { showToast('warn', 'Ledger code required', 'Enter a unique ledger code, e.g. 1110'); return; }
    setBusy(true);
    try {
      await createBankAccount({ name: name.trim(), currency: 'INR', ledger_code: code.trim(), ...(masked.trim() ? { account_number_masked: masked.trim() } : {}) });
      onDone();
    } catch { setBusy(false); }
  };

  const input = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 5, height: 36, paddingHorizontal: 11, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) };
  const label = { fontFamily: font.sans, fontSize: 12, color: colors.ink2, marginBottom: 4, fontWeight: '500' as const };

  return (
    <ModalShell visible={visible} onClose={onClose} title="Add bank account" subtitle="Creates the account and its ledger asset" width={460}
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 34, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable onPress={submit} disabled={busy} style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, justifyContent: 'center', backgroundColor: busy ? colors.paper3 : colors.accent }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: busy ? colors.ink4 : '#fff' }}>{busy ? 'Adding…' : 'Add account'}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 12 }}>
        <View>
          <Text style={label}>Account name *</Text>
          <TextInput value={name} onChangeText={setName} placeholder="e.g. HDFC Current A/C" placeholderTextColor={colors.ink4} style={input} />
        </View>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={label}>Ledger code *</Text>
            <TextInput value={code} onChangeText={setCode} placeholder="1110" placeholderTextColor={colors.ink4} style={input} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={label}>A/C number (last 4)</Text>
            <TextInput value={masked} onChangeText={setMasked} placeholder="••1234" placeholderTextColor={colors.ink4} style={input} />
          </View>
        </View>
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>Ledger code must be unique in your chart of accounts. Currency is ₹ (INR).</Text>
      </View>
    </ModalShell>
  );
};

/* ── Import a statement (manual rows → CSV → backend) ────────────────── */
interface Row { date: string; desc: string; amount: string; }
const ImportStatementModal: React.FC<{ visible: boolean; accountId: string | null; onClose: () => void; onDone: () => void }> = ({ visible, accountId, onClose, onDone }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const today = new Date().toISOString().slice(0, 10);
  const blank = (): Row => ({ date: today, desc: '', amount: '' });
  const [rows, setRows] = useState<Row[]>([blank(), blank(), blank()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (visible) { setRows([blank(), blank(), blank()]); setBusy(false); } }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, blank()]);
  const delRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const valid = rows.filter((r) => r.date && r.desc.trim() && r.amount.trim() && !isNaN(parseFloat(r.amount.replace(/,/g, ''))));

  const submit = async () => {
    if (busy || !accountId) return;
    if (valid.length === 0) { showToast('warn', 'Add at least one line', 'Each line needs a date, description and amount'); return; }
    // Build a CSV the backend parser understands (columns: date, description, amount).
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const csv = ['date,description,amount', ...valid.map((r) => `${r.date},${esc(r.desc.trim())},${parseFloat(r.amount.replace(/,/g, ''))}`)].join('\n');
    const base64 = btoa(unescape(encodeURIComponent(csv))); // UTF-8 safe base64 (web)
    setBusy(true);
    try {
      const res = await importStatement(accountId, { file_name: `statement-${today}.csv`, format: 'CSV', content_base64: base64 });
      showToast('success', 'Statement imported', `${res.line_count} lines · ${res.auto_matched} auto-matched · ${res.suggested} suggested`);
      onDone();
    } catch { setBusy(false); }
  };

  const cell = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 5, height: 34, paddingHorizontal: 9, fontFamily: font.sans, fontSize: 12, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) };

  return (
    <ModalShell visible={visible} onClose={onClose} title="Import statement" subtitle="Enter the bank lines — money out is negative" width={600}
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 34, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable onPress={submit} disabled={busy} style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, justifyContent: 'center', backgroundColor: busy ? colors.paper3 : colors.accent }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: busy ? colors.ink4 : '#fff' }}>{busy ? 'Importing…' : `Import ${valid.length || ''} lines`}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 8 }}>
        <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 2 }}>
          <Text style={{ width: 140, fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>Date</Text>
          <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>Description</Text>
          <Text style={{ width: 110, fontFamily: font.sans, fontSize: 11, color: colors.ink3, textAlign: 'right' }}>Amount (₹)</Text>
          <View style={{ width: 26 }} />
        </View>
        {rows.map((r, i) => (
          <View key={i} style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <View style={{ width: 140 }}><DateField value={r.date} onChange={(v) => setRow(i, { date: v })} /></View>
            <TextInput value={r.desc} onChangeText={(t) => setRow(i, { desc: t })} placeholder="e.g. NEFT CR COROMANDEL" placeholderTextColor={colors.ink4} style={[cell, { flex: 1 }]} />
            <TextInput value={r.amount} onChangeText={(t) => setRow(i, { amount: t })} keyboardType="numbers-and-punctuation" placeholder="70800 / -500" placeholderTextColor={colors.ink4} style={[cell, { width: 110, textAlign: 'right', fontFamily: font.mono }]} />
            <Pressable onPress={() => delRow(i)} style={{ width: 26, alignItems: 'center' }}><SkIcon name="x" size={14} color={colors.ink4} /></Pressable>
          </View>
        ))}
        <Pressable onPress={addRow} style={{ alignSelf: 'flex-start', height: 30, paddingHorizontal: 11, justifyContent: 'center', borderWidth: 1, borderColor: colors.line, borderRadius: 5, marginTop: 2 }}>
          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>+ Add line</Text>
        </Pressable>
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 4, lineHeight: 16 }}>
          Tip: enter amounts that match payments you’ve recorded (e.g. a ₹70,800 receipt) — the system auto-matches them. Money paid out is a negative amount.
        </Text>
      </View>
    </ModalShell>
  );
};

export default Banking;
